// src/components/CourseSelector.js
import React, { useState, useEffect, useCallback } from "react";
import { Paper, TextField } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase-config";

const CourseSelector = ({ onCourseSelect }) => {
  const [courseSearch, setCourseSearch] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseList, setCourseList] = useState([]);
  const [roundDate, setRoundDate] = useState("");

  // Use the deployed Cloud Functions endpoint as the default
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "https://api-enj2cxum7q-uc.a.run.app";

  // Wrap the API fetch function in useCallback
  const fetchAPICourses = useCallback(async () => {
    let apiCourses = [];
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/courses?name=${encodeURIComponent(courseSearch)}`
      );
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();
      if (data.courses && data.courses.length > 0) {
        apiCourses = data.courses.map((course) => ({
          id: course.id,
          clubName: course.club_name,
          courseName: course.course_name,
          location: `${course.location.city}, ${course.location.state}`,
          tees: course.tees, // raw tee data from API
        }));
      }
    } catch (error) {
      console.error("Error fetching courses from API:", error);
    }
    return apiCourses;
  }, [courseSearch, API_BASE_URL]);

  // Wrap the Firestore fetch function in useCallback
  const fetchFirestoreCourses = useCallback(async () => {
    let fsCourses = [];
    try {
      const coursesRef = collection(db, "courses");
      let q;
      if (courseSearch.trim() !== "") {
        q = query(
          coursesRef,
          where("course_name", ">=", courseSearch),
          where("course_name", "<=", courseSearch + "\uf8ff")
        );
      } else {
        q = coursesRef;
      }
      const snapshot = await getDocs(q);
      fsCourses = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          clubName: data.club_name,
          courseName: data.course_name,
          location: data.location
            ? `${data.location.city}, ${data.location.state}`
            : "",
          tees: data.tees,
        };
      });
    } catch (error) {
      console.error("Error fetching courses from Firestore:", error);
    }
    return fsCourses;
  }, [courseSearch]);

  // Merge the API and Firestore results. Note that both functions
  // are now stable, so we simply include them in the dependency array.
  const fetchMergedCourses = useCallback(async () => {
    const [apiCourses, fsCourses] = await Promise.all([
      fetchAPICourses(),
      fetchFirestoreCourses(),
    ]);
    const mergedCourses = [...apiCourses, ...fsCourses];
    setCourseList(mergedCourses);
  }, [fetchAPICourses, fetchFirestoreCourses]);

  // Debounce the search input and fetch merged courses.
  useEffect(() => {
    if (courseSearch.trim() === "") {
      setCourseList([]);
      return;
    }
    const delayDebounce = setTimeout(fetchMergedCourses, 500);
    return () => clearTimeout(delayDebounce);
  }, [courseSearch, fetchMergedCourses]);

  // When a course is selected in the Autocomplete,
  // build the display string and compute default hole data.
  const handleCourseSelection = (courseObj) => {
    if (!courseObj || !courseObj.courseName) return;
    const selectedDisplay = `${courseObj.clubName} - ${courseObj.courseName} (${courseObj.location})`;
    setSelectedCourse(courseObj);
    setCourseSearch(selectedDisplay);
    let defaultHoleData = Array(18).fill({ par: "", yardage: "", handicap: "" });
    if (courseObj.tees) {
      if (courseObj.tees.male && courseObj.tees.male.length > 0) {
        defaultHoleData = courseObj.tees.male[0].holes;
      } else if (courseObj.tees.female && courseObj.tees.female.length > 0) {
        defaultHoleData = courseObj.tees.female[0].holes;
      }
    }
    onCourseSelect(courseObj, roundDate, defaultHoleData);
  };

  // Handle input changes for Autocomplete.
  // When the input is cleared or reset, clear the course selection and round date.
  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === "clear" || reason === "reset") {
      setSelectedCourse(null);
      setCourseSearch("");
      setRoundDate("");
      onCourseSelect(null, "", Array(18).fill({ par: "", yardage: "", handicap: "" }));
      return;
    }
    if (reason === "input") {
      const currentDisplay = selectedCourse
        ? `${selectedCourse.clubName} - ${selectedCourse.courseName} (${selectedCourse.location})`
        : "";
      if (selectedCourse && newInputValue.trim() === currentDisplay.trim()) {
        setCourseSearch(newInputValue);
      } else {
        setSelectedCourse(null);
        setCourseSearch(newInputValue);
      }
    }
  };

  // Compute today's local date in YYYY-MM-DD format.
  const getTodayLocalString = () => {
    const today = new Date();
    const tzOffset = today.getTimezoneOffset() * 60000;
    return new Date(today.getTime() - tzOffset).toISOString().split("T")[0];
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
      <Autocomplete
        freeSolo
        value={selectedCourse}
        onChange={(event, newValue) => {
          if (typeof newValue === "object" && newValue !== null) {
            handleCourseSelection(newValue);
          }
        }}
        onInputChange={handleInputChange}
        inputValue={courseSearch}
        options={courseList}
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : `${option.clubName} - ${option.courseName} (${option.location})`
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search for Course"
            variant="outlined"
            fullWidth
            margin="normal"
          />
        )}
      />
      <TextField
        label="Date Played"
        type="date"
        value={roundDate}
        onChange={(e) => {
          setRoundDate(e.target.value);
          if (selectedCourse) {
            let defaultHoleData = Array(18).fill({ par: "", yardage: "", handicap: "" });
            if (selectedCourse.tees) {
              if (selectedCourse.tees.male && selectedCourse.tees.male.length > 0) {
                defaultHoleData = selectedCourse.tees.male[0].holes;
              } else if (selectedCourse.tees.female && selectedCourse.tees.female.length > 0) {
                defaultHoleData = selectedCourse.tees.female[0].holes;
              }
            }
            onCourseSelect(selectedCourse, e.target.value, defaultHoleData);
          }
        }}
        InputLabelProps={{ shrink: true }}
        fullWidth
        margin="normal"
        inputProps={{ max: getTodayLocalString() }}
      />
    </Paper>
  );
};

export default CourseSelector;